import { adminApi } from "@/lib/axios";

export interface LandingPageStats {
  totalVisits: number;
  uniqueVisitors: number;
  topPages: Array<{ page: string; visits: number }>;
  topEvents: Array<{ eventType: string; count: number }>;
  deviceBreakdown: Record<string, number>;
  trafficByDay: Record<string, number>;
}

export interface RecentActivity {
  id: string;
  sessionId: string;
  eventType: string;
  page: string;
  eventData?: Record<string, any>;
  device?: string;
  os?: string;
  browser?: string;
  createdAt: string;
}

export interface VisitsOverTime {
  date: string;
  visits: number;
}

export interface EventDetail {
  id: string;
  sessionId: string;
  eventData?: Record<string, any>;
  device?: string;
  country?: string;
  createdAt: string;
}

class LandingPageAnalyticsService {
  async getStats(startDate: Date, endDate: Date): Promise<LandingPageStats> {
    try {
      const response = await adminApi.get('/landing-page-analytics/stats', {
        params: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        },
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch landing page stats:', error);
      throw error;
    }
  }

  async getVisitsOverTime(
    startDate: Date,
    endDate: Date,
    groupBy: 'day' | 'week' | 'month' = 'day'
  ): Promise<VisitsOverTime[]> {
    try {
      const response = await adminApi.get('/landing-page-analytics/visits-over-time', {
        params: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          groupBy,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch visits over time:', error);
      throw error;
    }
  }

  async getEventDetails(
    eventType: string,
    startDate: Date,
    endDate: Date
  ): Promise<EventDetail[]> {
    try {
      const response = await adminApi.get('/landing-page-analytics/event-details', {
        params: {
          eventType,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        },
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch event details:', error);
      throw error;
    }
  }

  async getRecentActivity(limit: number = 50): Promise<RecentActivity[]> {
    try {
      const response = await adminApi.get('/landing-page-analytics/recent-activity', {
        params: { limit },
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch recent activity:', error);
      throw error;
    }
  }

  async clearOldData(daysToKeep: number = 90): Promise<void> {
    try {
      await adminApi.post('/landing-page-analytics/clear-old-data', {
        daysToKeep,
      });
    } catch (error) {
      console.error('Failed to clear old data:', error);
      throw error;
    }
  }
}

export const landingPageAnalyticsService = new LandingPageAnalyticsService();
